/** Trailing window of the MCP daily series (the strip / chart). */
export const MCP_HISTORY_DAYS = 90

/** How many tools / clients / reasons the breakdown lists show. */
export const MCP_TOOL_LIST_LIMIT = 8
export const MCP_CLIENT_LIST_LIMIT = 5
export const MCP_REASON_LIST_LIMIT = 3

/**
 * Display names for the AI clients as they name themselves on the MCP
 * `initialize` handshake (the `clientName` the servers stamp on every
 * event). Unknown names render as-is so a new client never shows blank.
 */
const MCP_CLIENT_LABELS: Record<string, string> = {
  "Anthropic/ClaudeAI": "Claude",
  Claude: "Claude",
  "claude-ai": "Claude",
  "Anthropic/Toolbox": "Claude",
  "Anthropic/API": "Claude (API)",
  "claude-code": "Claude Code",
  "sheet-add-in": "Claude in Excel / Sheets",
  "openai-mcp (Codex)": "ChatGPT / Codex",
  "openai-mcp": "ChatGPT / Codex",
  "codex-mcp-client": "ChatGPT / Codex",
  mcs: "Copilot Studio",
  "gemini-cli-mcp-client": "Gemini CLI",
  "placepoint-fusion-agent": "Fusion Agent",
}

export function formatMcpClient(client: string) {
  if (client === "") return "Unknown client"
  return MCP_CLIENT_LABELS[client] ?? client
}

/**
 * The use-case areas the report groups MCP tools into — the six "Bruk"
 * tiles of the one-off report, plus the Dataset MCP's own. Presentation
 * taxonomy for the MCP report only: the console's commercial areas (gold's
 * feature_area_map) answer a different question and stay as they are.
 */
export type McpToolGroup =
  | "property"
  | "search"
  | "company"
  | "plans"
  | "maps"
  | "other"
  | "datasets"
  | "rows"
  | "uploads"
  | "projects"
  | "areas"

export const MCP_TOOL_GROUP_LABELS: Record<McpToolGroup, string> = {
  property: "Property & land register",
  search: "Search, analysis & export",
  company: "Companies & ownership",
  plans: "Plans & zoning",
  maps: "Maps, layers & geography",
  other: "Help, templates & other",
  datasets: "Datasets",
  rows: "Rows & features",
  uploads: "Files & icons",
  projects: "Projects & sharing",
  areas: "Areas & geometry",
}

type McpTool = { label: string; group: McpToolGroup }

function defineTools(
  group: McpToolGroup,
  labels: Record<string, string>,
): Record<string, McpTool> {
  return Object.fromEntries(
    Object.entries(labels).map(([code, label]) => [code, { label, group }]),
  )
}

/**
 * What each MCP tool does, in the words a sales or CS person uses — the
 * report names "Land register extract", never `get_grunnbokutskrift` — and
 * the area it belongs to. The code stays visible as a sub-label for anyone
 * who needs to match it to the server's tool list. Tools not listed here
 * render a prettified code (underscores to spaces) under "Help, templates &
 * other", so a new tool never shows blank.
 */
const MCP_TOOLS: Record<string, McpTool> = {
  // Placepoint MCP: property and land register
  ...defineTools("property", {
    get_grunnbokutskrift: "Land register extract",
    search_cadastre_by_address: "Property by address",
    get_document: "Property document",
    get_property_transactions: "Property transactions",
    get_property_transaction_buyers: "Transaction buyers",
    get_property_transaction_sellers: "Transaction sellers",
    get_tenant_panel: "Tenant panel",
    find_neighbours: "Neighbouring properties",
  }),
  // Search, analysis and export over the property index
  ...defineTools("search", {
    search: "Property search",
    nested_select: "Field extraction",
    nested_aggregate: "Field aggregation",
    nested_top_n: "Top-N fields",
    count: "Count properties",
    aggregate: "Aggregate properties",
    distinct_values: "Distinct values",
    exact_sum: "Exact sum",
    find_field: "Find field",
    list_filter_options: "Filter options",
    list_presets: "Search presets",
    get_index_schema: "Index schema",
    get_nested_schema: "Nested schema",
    list_indexes: "List indexes",
    export: "Export to file",
  }),
  // Companies and ownership
  ...defineTools("company", {
    company_dossier: "Company dossier",
    search_companies: "Company search",
    get_company_document: "Company document",
    lookup_company_brreg: "Company lookup (Brønnøysundregistrene)",
    get_aarsregnskap_pdf: "Annual accounts (PDF)",
    get_company_roles: "Company roles",
    get_person_roles: "Person's roles",
    get_companies_by_role_holder: "Companies by role holder",
    get_company_shareholders: "Shareholders",
    get_company_shareholders_history: "Shareholder history",
    get_company_shareholders_years: "Shareholder years",
    get_company_ownership_chain: "Ownership chain",
    get_company_portfolio: "Company portfolio",
    get_company_property_holdings: "Company property holdings",
    get_ownership_lookup_codes: "Ownership lookup codes",
    render_ownership_tree: "Ownership tree (image)",
  }),
  // Plans and zoning
  ...defineTools("plans", {
    get_property_plans: "Plans for property",
    get_plan: "Plan",
    get_plan_documents: "Plan documents",
    get_plan_dossier: "Plan dossier",
    get_plan_coverage: "Plan coverage",
    get_plan_coverage_geometry: "Plan coverage (geometry)",
    fetch_plan_document: "Fetch plan document",
    search_plans: "Plan search",
    find_plan_filter_values: "Plan filter values",
    render_plan_map: "Plan map (image)",
  }),
  // Map layers, geography and travel
  ...defineTools("maps", {
    query_map_layer: "Map layer query",
    map_layer_lookup: "Map layer lookup",
    map_layer_intersects: "Map layer intersects",
    map_layer_risk_sweep: "Risk sweep across map layers",
    search_map_layers: "Find map layers",
    get_map_layer: "Map layer",
    get_map_layer_service: "Map layer service",
    list_map_layer_services: "Map layer services",
    find_map_layer_filter_values: "Map layer filter values",
    render_map_layer_image: "Map layer image",
    render_static_map: "Static map",
    render_static_map_with_layers: "Static map with layers",
    render_imagery: "Aerial imagery",
    list_imagery_projects: "Imagery projects",
    list_base_map_styles: "Base map styles",
    map_view: "Map view",
    geocode_address: "Geocode address",
    reverse_geocode: "Reverse geocode",
    search_place: "Place search",
    resolve_area: "Resolve area",
    geo_search: "Geographic search",
    geo_intersect: "Geometry intersect",
    geo_union: "Geometry union",
    geo_difference: "Geometry difference",
    geo_buffer: "Geometry buffer",
    geo_simplify: "Geometry simplify",
    geo_bbox_of: "Bounding box",
    geo_aggregate: "Geographic aggregation",
    geo_to_odata_filter: "Geometry to filter",
    search_poi: "Points of interest",
    search_poi_category: "Points of interest by category",
    list_poi_categories: "POI categories",
    get_traffic_flow: "Traffic flow",
    get_traffic_flow_along_route: "Traffic along route",
    get_traffic_incidents: "Traffic incidents",
    traveltime_isochrone: "Travel-time area",
    traveltime_matrix: "Travel-time matrix",
    traveltime_directions: "Directions",
    traveltime_reachable_grid: "Reachable grid",
    traveltime_capabilities: "Travel-time capabilities",
  }),
  // Reports, branding and help
  ...defineTools("other", {
    report_recipe: "Report recipe",
    get_cover_pack: "Cover pack",
    cover_layout_guide: "Cover layout guide",
    cover_metadata_block: "Cover metadata",
    render_bar_chart: "Bar chart (image)",
    render_kpi_strip: "KPI strip (image)",
    render_brand_background: "Brand background (image)",
    get_brand_assets: "Brand assets",
    pre_writing_check: "Pre-writing check",
    get_usage_guide: "Usage guide",
    get_tool_help: "Tool help",
    get_mcp_context: "MCP context",
  }),
  // Dataset MCP: datasets
  ...defineTools("datasets", {
    list_datasets: "List datasets",
    search_datasets: "Dataset search",
    get_dataset: "Dataset",
    get_dataset_columns: "Dataset columns",
    get_dataset_style: "Dataset style",
    get_dataset_matching_log: "Dataset matching log",
    create_dataset: "Create dataset",
    update_dataset: "Update dataset",
    delete_dataset: "Delete dataset",
    copy_dataset: "Copy dataset",
    promote_dataset: "Promote dataset",
    export_dataset: "Export dataset",
    update_dataset_columns: "Update dataset columns",
    add_dataset_column: "Add dataset column",
    set_dataset_column_visibility: "Column visibility",
    update_dataset_style: "Update dataset style",
    set_dataset_matching: "Dataset matching",
    rerun_dataset_matching: "Rerun matching",
  }),
  // Dataset MCP: rows and features
  ...defineTools("rows", {
    get_dataset_features: "Dataset features",
    list_dataset_rows: "List dataset rows",
    count_dataset_rows: "Count dataset rows",
    update_dataset_rows: "Update dataset rows",
    update_dataset_row_attributes: "Update row attributes",
    delete_dataset_rows: "Delete dataset rows",
    upsert_dataset_features: "Upsert dataset features",
  }),
  // Dataset MCP: files and icons
  ...defineTools("uploads", {
    update_dataset_from_upload: "Update dataset from file",
    create_upload: "Upload file",
    inspect_upload: "Inspect upload",
    ingest_upload: "Ingest upload",
    delete_upload: "Delete upload",
    create_icon_upload: "Upload icon",
    import_icon: "Import icon",
    list_icons: "List icons",
    delete_icon: "Delete icon",
  }),
  // Dataset MCP: projects and sharing
  ...defineTools("projects", {
    list_projects: "List projects",
    list_projects_with_datasets: "Projects with datasets",
    get_project: "Project",
    create_project: "Create project",
    update_project: "Update project",
    delete_project: "Delete project",
    set_active_project: "Set active project",
    grant_project_access: "Grant project access",
    revoke_project_access: "Revoke project access",
    share_project_with_tenant: "Share project with team",
    list_tenant_members: "Team members",
  }),
  // Dataset MCP: areas and geometry
  ...defineTools("areas", {
    aggregate_dataset_by_area: "Aggregate dataset by area",
    add_areas_to_dataset: "Add areas to dataset",
    search_areas: "Area search",
    get_area_geometry: "Area geometry",
    list_area_types: "Area types",
    export_areas: "Export areas",
    geo_measure: "Measure geometry",
    traveltime_optimize_route: "Optimise route",
  }),
}

export function formatMcpTool(tool: string) {
  if (tool === "") return "Unknown tool"
  return MCP_TOOLS[tool]?.label ?? tool.replaceAll("_", " ")
}

export function mcpToolGroup(tool: string): McpToolGroup {
  return MCP_TOOLS[tool]?.group ?? "other"
}

/**
 * The definitions behind every MCP figure, the report's methodology page.
 * One list for the Portfolio report and the customer and user sections, so
 * a number means the same thing wherever it is read.
 */
export const MCP_REPORT_NOTES: { term: string; text: string }[] = [
  {
    term: "Tool call",
    text:
      "One request from an AI client to one MCP tool. A question in Claude " +
      "or ChatGPT usually fans out into several calls — a search first, then " +
      "documents, then a map or the land register — so calls measure the work " +
      "the server did, not the questions asked.",
  },
  {
    term: "Person",
    text:
      "A signed-in Placepoint user. The same person counts once however many " +
      "AI clients they use.",
  },
  {
    term: "Customer",
    text:
      "The organisation the person belongs to in Placepoint. Placepoint's own " +
      "people are left out of every customer figure and shown separately as " +
      "internal calls.",
  },
  {
    term: "Since the first call",
    text:
      "Totals run from the first tracked call, 4 September 2026 for the " +
      "Placepoint MCP. 'Last 28 days' and 'last 7 days' end today; a trend " +
      "compares the last 28 days with the 28 before.",
  },
  {
    term: "Week",
    text:
      "Monday to Sunday. The current week is marked 'so far' and never used " +
      "for a trend.",
  },
  {
    term: "Refused sign-in",
    text:
      "Someone tried to use the MCP without a seat on their customer's MCP " +
      "license and was turned away before any tool ran. The AI client retries " +
      "on its own, so attempts say how persistent someone was, not how many " +
      "questions they asked. Refusals never count as calls.",
  },
  {
    term: "Failed call",
    text:
      "A call the server accepted but could not complete — usually an AI " +
      "client sending an argument the tool does not understand, or an " +
      "upstream source timing out. Counted as a call.",
  },
  {
    term: "A working day",
    text:
      "The median over every day a person made at least one call: how many " +
      "calls, and how many different tools, a typical day holds.",
  },
]
